import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSettings } from "@helm/core";
import {
  TIMEFRAME_PRESETS,
  formatTimeframe,
  migrateTimeframe,
  parseTimeframe,
  parseTimeframeInput,
  sanitizeFavorites,
  sortTimeframes,
  timeframeTitle,
  toggleFavorite,
} from "@helm/core/bars";
import { settingsSync } from "../session";

/** Default chart timeframe (1 minute). */
export const DEFAULT_TIMEFRAME = "1m";

/** Canonical form of a stored timeframe (old layouts included); falls back to 1 minute. */
export function canonicalTimeframe(tf: string): string {
  return migrateTimeframe(tf) ?? DEFAULT_TIMEFRAME;
}

function titleOf(tf: string): string {
  const s = parseTimeframe(tf);
  return s ? timeframeTitle(s) : tf;
}

/**
 * Result of the custom box: the canonical timeframe, or an error to show. Accepts "7m", "377t",
 * "8r", "1000v", "4rk", "1D", "1W", "3mo", "1M" (month), a bare number (minutes)…
 */
export function readCustomTimeframe(text: string): { tf: string } | { error: string } {
  const spec = parseTimeframeInput(text);
  if (spec) return { tf: formatTimeframe(spec) };
  return {
    error:
      "Not a timeframe. Try 7m, 4h, 1D, 2D, 1W, 3W, 1mo, 377t, 8r, 1000v or 4rk (1 s–12 h; ≤ 30 days; ≤ 52 weeks; months 1/2/3/4/6/12; ≤ 100 000 trades; ≤ 10 000 ticks).",
  };
}

/**
 * Chart header timeframe picker (Tradesea / TradingView style): the user's pinned favourites as
 * buttons (synced settings, `chart_tf_favorites`), the current timeframe if it is not pinned, and
 * a menu with every preset grouped by bar type, star toggles to pin/unpin, and a custom box. With
 * `onRangePhantomsChange` the Range group carries the chart's phantom-bar toggle (Q-T5).
 */
export function TimeframePicker({
  value,
  onChange,
  rangePhantoms = true,
  onRangePhantomsChange,
  compact = false,
}: {
  value: string;
  onChange: (tf: string) => void;
  rangePhantoms?: boolean;
  onRangePhantomsChange?: (on: boolean) => void;
  /**
   * Only the current timeframe plus the menu (chart headers while the window-wide toolbar shows
   * the pinned favourites, Q-G3); clicking the current timeframe opens the menu too.
   */
  compact?: boolean;
}): React.JSX.Element {
  const stored = useSettings((s) => s.settings.chart_tf_favorites);
  const favs = sanitizeFavorites(stored);
  const current = canonicalTimeframe(value);
  const [open, setOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const shown = compact ? [] : favs.includes(current) ? favs : sortTimeframes([...favs, current]);
  const pick = (tf: string): void => {
    setOpen(false);
    if (tf !== current || value !== current) onChange(tf);
  };
  const toggle = (tf: string): void => {
    void settingsSync.update({ chart_tf_favorites: toggleFavorite(favs, tf) });
  };
  return (
    <span className="tf-picker" role="group" aria-label="Timeframe">
      {shown.map((tf) => (
        <button
          key={tf}
          type="button"
          className={tf === current ? "tf active" : "tf"}
          aria-pressed={tf === current}
          title={titleOf(tf)}
          onClick={() => {
            pick(tf);
          }}
        >
          {tf}
        </button>
      ))}
      <button
        ref={moreRef}
        type="button"
        className={open ? "tf tf-more active" : "tf tf-more"}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={
          compact
            ? `${titleOf(current)} — all timeframes, bar types and a custom value`
            : "All timeframes, bar types and a custom value"
        }
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        {compact ? `${current} ▾` : "▾"}
      </button>
      {open && (
        <TimeframeMenu
          anchor={moreRef.current}
          current={current}
          favs={favs}
          onPick={pick}
          onToggle={toggle}
          rangePhantoms={rangePhantoms}
          onRangePhantomsChange={onRangePhantomsChange}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </span>
  );
}

function TimeframeMenu({
  anchor,
  current,
  favs,
  onPick,
  onToggle,
  rangePhantoms,
  onRangePhantomsChange,
  onClose,
}: {
  anchor: HTMLElement | null;
  current: string;
  favs: readonly string[];
  onPick: (tf: string) => void;
  onToggle: (tf: string) => void;
  rangePhantoms: boolean;
  onRangePhantomsChange: ((on: boolean) => void) | undefined;
  onClose: () => void;
}): React.JSX.Element {
  const boxRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxH: number }>({ left: 0, top: 0, maxH: 400 });
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fixed-position portal: dockview panels clip overflow, the menu must not be cut off.
  useLayoutEffect(() => {
    const place = (): void => {
      const r = anchor?.getBoundingClientRect();
      const w = boxRef.current?.offsetWidth ?? 320;
      if (!r) return;
      const left = Math.max(4, Math.min(r.left, window.innerWidth - w - 4));
      const top = r.bottom + 4;
      setPos({ left, top, maxH: Math.max(160, window.innerHeight - top - 8) });
    };
    place();
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("resize", place);
    };
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node | null;
      if (t && (boxRef.current?.contains(t) || anchor?.contains(t))) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  const presets = new Set(TIMEFRAME_PRESETS.flatMap((g) => g.items));
  const custom = sortTimeframes([...new Set([current, ...favs])].filter((tf) => !presets.has(tf)));
  const item = (tf: string): React.JSX.Element => {
    const pinned = favs.includes(tf);
    return (
      <span key={tf} className="tf-item">
        <button
          type="button"
          className={tf === current ? "tf-chip active" : "tf-chip"}
          title={titleOf(tf)}
          onClick={() => {
            onPick(tf);
          }}
        >
          {tf}
        </button>
        <button
          type="button"
          className={pinned ? "tf-star pinned" : "tf-star"}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${tf} from the header` : `Pin ${tf} to the header`}
          title={pinned ? "Unpin from the header" : "Pin to the header"}
          onClick={() => {
            onToggle(tf);
          }}
        >
          {pinned ? "★" : "☆"}
        </button>
      </span>
    );
  };

  return createPortal(
    <div
      ref={boxRef}
      className="tf-menu"
      role="dialog"
      aria-label="Timeframe"
      style={{ left: pos.left, top: pos.top, maxHeight: pos.maxH }}
    >
      <form
        className="tf-custom"
        onSubmit={(e) => {
          e.preventDefault();
          const r = readCustomTimeframe(text);
          if ("tf" in r) onPick(r.tf);
          else setError(r.error);
        }}
      >
        <input
          autoFocus
          className="tf-custom-input"
          value={text}
          placeholder="Custom: 7m, 4h, 2D, 3W, 377t, 8r, 1000v, 4rk"
          aria-label="Custom timeframe"
          aria-invalid={error !== null}
          spellCheck={false}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
        />
        <button type="submit" className="tf-chip">
          Apply
        </button>
      </form>
      {error && (
        <div className="tf-error" role="alert">
          {error}
        </div>
      )}
      {custom.length > 0 && (
        <div className="tf-group">
          <div className="tf-group-label">Custom</div>
          <div className="tf-grid">{custom.map(item)}</div>
        </div>
      )}
      {TIMEFRAME_PRESETS.map((g) => (
        <div key={g.group} className="tf-group">
          <div className="tf-group-label">{g.label}</div>
          <div className="tf-grid">{g.items.map(item)}</div>
          {g.group === "Range" && onRangePhantomsChange && (
            <label
              className="tf-option"
              title="Fill price gaps with phantom bars (no volume) so no level is skipped"
            >
              <input
                type="checkbox"
                checked={rangePhantoms}
                onChange={(e) => {
                  onRangePhantomsChange(e.target.checked);
                }}
              />
              Phantom bars (fill gaps) on this chart
            </label>
          )}
        </div>
      ))}
      <div className="tf-hint">
        Tip: with the chart focused, type a number and a unit (e.g. 7m, 377t) and press Enter.
      </div>
    </div>,
    document.body,
  );
}
